import { createContext, useContext, useState, useRef, useEffect } from 'react';
import {
  fonts,
  bgColors,
  sizes,
  stands,
  hangings,
  packagings,
  multipleLayouts,
  multipleLayoutsH,
} from '../glassConsts';
import { computeOverlay } from '../utils/overlayMath';
import {
  onZoomChange,
  updateAt,
} from './Configurator/Preview/MultipleLayouts/helpers';

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [general, setGeneral] = useState({
    // původní "general" stavy:
    envLoaded: false,
    mainText: '',
    mainTextBreak: false,
    hasSecondaryText: false,
    secondaryText: '',
    font: fonts[0].name,
    bg: bgColors[0].code,
    size: sizes[0].value,
    stand: stands[0].value,
    hanging: hangings[0].value,
    packaging: packagings[0].value,
    color: 'white',
    soundLike: false,

    isAdmin: false,
    isHeart: false,
    glassType: 'classic',

    imageSrc: '',
    crop: { x: 0, y: 0 },
    rotation: 0,
    zoom: 1,
    croppedAreaPixels: null,
    croppedImage: null,
    orientationHorizontal: false,

    // layout / prostředí
    containerSize: { width: 1000, height: 800 },
    pluginUrl: window.GBCONF ? window.GBCONF.pluginUrl : '',
    ajaxurl: window.GBCONF ? window.GBCONF.ajaxurl : '/wp-admin/admin-ajax.php',
    nonce: window.GBCONF ? window.GBCONF.nonce : '',
    cartKey: false,

    // admin tool (clipboard UI)
    value: '',
    busy: false,
    error: '',

    // orezy a hovna
    selectedCarouselCropIndex: 0,
    selectedCarouselNameDesignIndex: 0,

    // pricing (ponecháno jako dříve)
    total: 799, // sizePrice + standPrice + hangingPrice + packagingPrice;
  });

  const [music, setMusic] = useState({
    // music state jako objekt
    name: '',
    interpret: '',
    timeStart: '1:19',
    timeEnd: '2:45',
  });

  const [socials, setSocials] = useState({
    // socials state jako objekt
    profilePicture: null,
    croppedAreaPixels: null,
    name: '',
    description: '',
    isLiked: false,
    likes: '',
    comments: '',
    shares: '',
    likesOthersCount: '',
    likesTag: '',
    isActiveStory: false,
    isVerified: false,
    zoom: 1,
    crop: { x: 0, y: 0 },
    useCustomStats: true,
  });

  const [multiple, setMultiple] = useState([]);
  const [multipleTextConfig, setMultipleTextConfig] = useState({
    text: '',
    useText: false,
  });

  const setMultipleConfigState = (patch) =>
    setMultipleTextConfig((p) => ({ ...p, ...patch }));
  const setGeneralState = (patch) =>
    setGeneral((p) => {
      console.log(p, patch);
      return { ...p, ...patch };
    });
  const setMusicState = (patch) => setMusic((p) => ({ ...p, ...patch }));
  const setSocialsState = (patch) => setSocials((p) => ({ ...p, ...patch }));

  const setMultipleState = (index, newValue) => {
    setMultiple((prev) => {
      if (index < prev.length) {
        return prev.map((item, i) => (i === index ? newValue : item));
      } else {
        const newArr = [...prev];
        newArr[index] = newValue;
        return newArr;
      }
    });
  };

  const [selectedLayout, setSelectedLayout] = useState(multipleLayouts[0]);
  const [multipleGap, setMultipleGap] = useState(0);
  const [croppersCount, setCroppersCount] = useState(selectedLayout.items || 2);
  const [selectedMultipleZoomIndex, setSelectedMultipleZoomIndex] =
    useState(null);

  const generateCroppers = () => {
    return Array.from({ length: 9 }, () => ({
      imageSrc: '',
      crop: { x: 0, y: 0 },
      zoom: 1,
    }));
  };

  useEffect(() => {
    setCroppersCount(selectedLayout.items);
  }, [selectedLayout]);

  useEffect(() => {
    if (general.orientationHorizontal) setSelectedLayout(multipleLayoutsH[0]);
    else setSelectedLayout(multipleLayouts[0]);
  }, [general.orientationHorizontal]);

  useEffect(() => {
    console.log(general.zoom);
  }, [general.zoom]);

  const [croppers, setCroppers] = useState(() => generateCroppers());

  const handleZoomPlus = (kind, index = null, value = null) => {
    if (kind === 'general') {
      console.log(general.zoom);
      setGeneralState({ zoom: +Number(general.zoom + 0.1).toFixed(1) });
    }
    if (kind === 'socials')
      setSocialsState({ zoom: +Number(socials.zoom + 0.1).toFixed(1) });
    if (kind === 'multiple')
      onZoomChange(
        setCroppers,
        index,
        +Number(croppers[selectedMultipleZoomIndex].zoom + 0.1).toFixed(1)
      );
    if (kind === 'multiple_full')
      onZoomChange(setCroppers, index, +Number(value).toFixed(1));
    if (kind === 'gap') setMultipleGap(+Number(multipleGap + 5).toFixed(1));
  };

  const handleZoomMinus = (kind, index = null) => {
    if (kind === 'general')
      setGeneralState({ zoom: +Number(general.zoom - 0.1).toFixed(1) });
    if (kind === 'socials')
      setSocialsState({ zoom: +Number(socials.zoom - 0.1).toFixed(1) });
    if (kind === 'multiple')
      onZoomChange(
        setCroppers,
        index,
        +Number(croppers[selectedMultipleZoomIndex].zoom - 0.1).toFixed(1)
      );
    if (kind === 'gap') setMultipleGap(+Number(multipleGap - 5).toFixed(1));
  };

  const containerRef = useRef(null);

  const measure = () => {
    if (containerRef.current) {
      setGeneralState({
        containerSize: {
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        },
      });
    }
  };
  // setTimeout(measure, 2000);

  //measure()
  window.addEventListener('load', measure, { once: true });

  // Změří aktuální velikost kontejneru (při resize)
  useEffect(() => {
    window.addEventListener('resize', measure);
    measure();
  }, []);

  useEffect(() => {
    measure();
  }, [general.glassType, general.orientationHorizontal]);

  const [size, setSize] = useState({ width: 0, height: 0 });
  const value = {
    general,
    music,
    socials,
    multiple,
    selectedMultipleZoomIndex,
    croppersCount,
    croppers,
    multipleGap,
    selectedLayout,
    setSelectedLayout,
    setMultipleGap,
    setCroppersCount,
    setCroppers,
    multipleTextConfig,
    setMultipleConfigState,
    setSelectedMultipleZoomIndex,
    setGeneralState,
    setMusicState,
    setSocialsState,
    setMultipleState,
    handleZoomMinus,
    handleZoomPlus,
    containerRef,
    size,
    setSize,
  };

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
}

export const useConfig = () => {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error('useConfig must be used within ConfigProvider');
  return ctx;
};
